import React from "react";
import { LegalShell, Section } from "../components/LegalShell";

const LAST_UPDATED = "26 February 2026";

export default function Legal() {
    return (
        <LegalShell
            eyebrow="Legal"
            title="Terms, Acceptable Use, Disclaimer & IP"
            subtitle="The legal terms on which EuroLaw Hub is provided. Read alongside the Privacy Policy and the GDPR & AI Act Transparency notice."
            lastUpdated={LAST_UPDATED}
        >
            <Section id="terms" title="1. Terms of Service">
                <p>
                    These Terms govern your access to and use of EuroLaw Hub (the
                    "Service"), operated by EuroLaw Hub ("we", "us"). By
                    creating an account or using the Service you agree to these
                    Terms. If you are using the Service on behalf of an
                    organisation, you confirm you are authorised to bind it.
                </p>
                <p>
                    <strong>1.1 The Service.</strong> EuroLaw Hub provides
                    AI-assisted EU legal research, compliance workspaces, editorial
                    bulletins and watchdog features. The runtime model is Anthropic
                    Claude, accessed only via our secure backend. Specific module
                    behaviour is described on our marketing pages and in the
                    in-product help.
                </p>
                <p>
                    <strong>1.2 Account.</strong> You must provide accurate
                    information at sign-up and keep your credentials confidential.
                    You are responsible for activity on your account. Notify us
                    immediately at altay@eurolawhub.com if you suspect compromise.
                </p>
                <p>
                    <strong>1.3 Plans, fees and billing.</strong> The Service is
                    offered on Free, Professional and Team plans with the credit
                    allowances and document caps published on the Pricing page.
                    Paid plans are billed monthly in advance via our secure payment
                    partner, which acts as the merchant of record (handling VAT/GST,
                    invoicing and refunds). Refunds and the EU 14-day withdrawal right
                    are described in our <a href="/refunds" className="underline">Refund Policy</a>.
                    Subscriptions renew automatically until cancelled. You can
                    cancel from /app/profile; cancellation takes effect at the end
                    of the current billing period.
                </p>
                <p>
                    <strong>1.4 Acceptable use.</strong> See Section 2 below.
                    Breaches may result in immediate suspension under Section 4.
                </p>
                <p>
                    <strong>1.5 Term and termination.</strong> Either party may
                    terminate the agreement at any time. We may suspend or
                    terminate access for material breach, abusive use, or where
                    required by law. On termination we cease processing your
                    personal data per the Privacy Policy retention schedule.
                </p>
                <p>
                    <strong>1.6 Warranties.</strong> Subject to mandatory consumer
                    rights, the Service is provided "as is" and "as available". We
                    do not warrant that outputs are accurate, complete, or fit for
                    any specific legal matter (see Section 3).
                </p>
                <p>
                    <strong>1.7 Liability.</strong> To the maximum extent permitted
                    by law, our aggregate liability for any claim arising under
                    these Terms is limited to the fees paid for the Service in the
                    twelve months preceding the event giving rise to the claim. We
                    are not liable for indirect or consequential losses, including
                    loss of profits, goodwill, business interruption or anticipated
                    savings. Nothing in these Terms excludes liability for fraud,
                    death or personal injury caused by negligence, or any liability
                    that cannot lawfully be excluded.
                </p>
                <p>
                    <strong>1.8 Governing law.</strong> These Terms are governed by
                    the laws of Ireland. The courts of Dublin have non-exclusive
                    jurisdiction, without prejudice to mandatory consumer-protection
                    rights in your Member State of habitual residence.
                </p>
                <p>
                    <strong>1.9 Changes.</strong> We may update these Terms; the
                    "Last updated" date reflects the current version. Material
                    changes will be notified by email or in-app banner.
                </p>
            </Section>

            <Section id="aup" title="2. Acceptable Use Policy">
                <p>You agree NOT to use EuroLaw Hub to:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-[15px]">
                    <li>
                        Generate content that infringes third-party intellectual
                        property, defames, harasses, or threatens any person.
                    </li>
                    <li>
                        Process special-category personal data (Article 9 GDPR)
                        about identifiable individuals other than your own clients
                        and only where you have an appropriate legal basis.
                    </li>
                    <li>
                        Pass-through unlawful content for the model to summarise or
                        translate.
                    </li>
                    <li>
                        Attempt to extract the model's prompts, fine-tune state, or
                        reconstruct our system prompts and instructions.
                    </li>
                    <li>
                        Circumvent rate limits, plan caps, or pay-walls (including
                        the 2-question public landing widget).
                    </li>
                    <li>
                        Reverse-engineer, scrape, or build a competing dataset from
                        our editorial Bulletins or curated workspace content.
                    </li>
                    <li>
                        Use the Service to make a solely-automated decision
                        producing legal or similarly significant effects on a
                        natural person (Art. 22 GDPR). EuroLaw Hub is decision
                        support and assumes a qualified human in the loop.
                    </li>
                    <li>
                        Use the Service to develop weapons, malicious code, or
                        anything otherwise prohibited by Anthropic's Usage
                        Policies.
                    </li>
                </ul>
                <p className="mt-4">
                    Suspected violations should be reported to{" "}
                    <a className="underline" href="mailto:altay@eurolawhub.com">
                        altay@eurolawhub.com
                    </a>
                    . We may suspend access immediately while we investigate.
                </p>
            </Section>

            <Section id="disclaimer" title="3. Disclaimer: information, not legal advice">
                <p>
                    EuroLaw Hub provides legal information and decision-support
                    tooling. It does not provide legal advice and using the Service
                    does not create a solicitor–client, attorney–client, or any
                    similar professional relationship.
                </p>
                <p>
                    Outputs are generated by a large language model and may contain
                    errors, omissions, or out-of-date references. You must verify
                    every output against the cited primary source before relying on
                    it. A qualified lawyer remains responsible for the legal
                    judgement applied to any specific matter.
                </p>
                <p>
                    Citations to EU sources (EUR-Lex, curia.europa.eu, EDPB, ENISA,
                    EBA, ESMA, the Commission, the Council, national supervisory
                    authorities) are provided to help you go to the source. If a
                    URL is missing it is because we could not confirm a stable
                    official link at the time the answer was generated.
                </p>
            </Section>

            <Section id="ip" title="4. Intellectual Property">
                <p>
                    <strong>4.1 Our content.</strong> The Service, including its
                    software, design system, editorial bulletins, curated workspace
                    data, decision-tree logic and brand assets, is owned by
                    EuroLaw Hub and protected by copyright, database rights and
                    related rights. You receive a non-exclusive, non-transferable,
                    revocable licence to use the Service per these Terms for your
                    own legal work. You may quote our bulletins with attribution
                    and a link back; you may not republish substantial portions.
                </p>
                <p>
                    <strong>4.2 Your content.</strong> You retain all rights in
                    documents you upload and prompts you submit. You grant us a
                    limited, non-exclusive licence solely to process them to
                    deliver the Service to you. We do not train Anthropic's models
                    on your content.
                </p>
                <p>
                    <strong>4.3 Generated output.</strong> Subject to the
                    Disclaimer in Section 3 and to Anthropic's policies governing
                    Claude output, you may use the answers and reports produced for
                    you in your own legal work, including in client deliverables,
                    provided that you verify them against the primary sources and
                    take professional responsibility for the result.
                </p>
                <p>
                    <strong>4.4 Trade marks.</strong> "EuroLaw Hub" and our logo
                    are unregistered trade marks of EuroLaw Hub. Other trade
                    marks (e.g. Claude, Anthropic, MongoDB) belong
                    to their respective owners and are used here only to identify
                    the relevant technologies.
                </p>
                <p>
                    <strong>4.5 DMCA / EU notice-and-action.</strong> If you
                    believe content on EuroLaw Hub infringes your rights, write to{" "}
                    <a className="underline" href="mailto:altay@eurolawhub.com">
                        altay@eurolawhub.com
                    </a>{" "}
                    with a description of the work, the location on the Service,
                    your contact details, and a statement of good-faith belief.
                </p>
            </Section>
        </LegalShell>
    );
}
